import { Grid } from "@mui/material";
import { useEffect, useState } from "react";

import Box from "../Box";
import Modal from "../Modal";
import Button from "../Button";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import ProfileService from "../../services/identity/profile.service";

import { ChangeMode } from "../../containers/profile";
import { IInput, IUserProfile } from "../../commons/interfaces";

interface IModelChangeEmail {
    email?: IInput,
    newEmail?: IInput,
    otpCode?: IInput,
}

enum ChangeEmailMode {
    EnterEmail = 0,
    EnterOTP = 1,
}

interface IChangeEmailProps {
    idHref: string;
    tenantCode: string,
    serviceCode: string,
    userProfile: IUserProfile;
    onChangeEmail: (email: string) => void;

    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onLoading: (value: boolean) => void;
}

const ChangeEmail = ({
    idHref,
    tenantCode,
    serviceCode,
    userProfile,
    onChangeEmail,

    onError,
    onSuccess,
    onLoading,
}: IChangeEmailProps) => {
    const strings = useStrings();
    const [model, setModel] = useState<IModelChangeEmail>({} as IModelChangeEmail);
    const [changeEmailMode, setChangeEmailMode] = useState<number>(ChangeEmailMode.EnterEmail);

    useEffect(() => {
        const newModel = {
            email: { value: userProfile?.email || "" },
        };

        setModel(newModel);
    }, [userProfile]);

    const onValueChange = (newValue: any, key: string) => {
        setModel({ ...model, [key]: { value: newValue } });
    };

    const checkValidate = (mode: number) => {
        let checked: boolean = true;
        let newData: any = { ...model };

        switch (mode) {
            case ChangeMode.Email:
                if (Helpers.isNullOrEmpty(newData?.["newEmail"]?.value)) {
                    newData["newEmail"] = { error: strings.Common.REQUIRED };
                    checked = false;
                } else if (!Helpers.isValidEmail(newData?.["newEmail"]?.value)) {
                    newData["newEmail"] = { error: strings.Common.INVALID_EMAIL };
                    checked = false;
                }
                setModel(newData);
                return checked;
            case ChangeMode.OtpCode:
                if (Helpers.isNullOrEmpty(newData?.["otpCode"]?.value)) {
                    newData["otpCode"] = { error: strings.Common.REQUIRED };
                    checked = false;
                }
                setModel(newData);
                return checked;
            default:
                break;
        }
    };

    const handleGetOTP = async () => {
        if (!checkValidate(ChangeMode.Email)) return;

        try {
            onLoading(true);

            const result = await ProfileService.getChangeEmailOTP({
                email: model.newEmail?.value || undefined,
                tenantCode: tenantCode,
                serviceCode: serviceCode,
            });

            if (result.statusCode === 200) {
                if (changeEmailMode === ChangeEmailMode.EnterEmail) {
                    setChangeEmailMode(ChangeEmailMode.EnterOTP);
                }
            }

            onLoading(false);
        } catch (error: any) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const handleChangeEmail = async () => {
        if (!checkValidate(ChangeMode.OtpCode)) return;

        try {
            onLoading(true);

            const result = await ProfileService.changeEmail({
                tenantCode: tenantCode,
                serviceCode: serviceCode,
                email: model.newEmail?.value || undefined,
                otp: model.otpCode?.value,
            });

            if (result.statusCode === 200) {
                setChangeEmailMode(ChangeEmailMode.EnterEmail);
                setModel({ email: model.newEmail });

                onChangeEmail(model.newEmail?.value || "");
                onSuccess(strings.Profile.CHANGE_EMAIL_SUCCESS);
            }

            onLoading(false);
        } catch (error: any) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const renderModalOTP = () => (
        <Modal
            fullWidth
            maxWidth="sm"
            visible={changeEmailMode === ChangeEmailMode.EnterOTP}
            onClose={() => {
                setChangeEmailMode(ChangeEmailMode.EnterEmail);
                setModel({
                    email: { value: userProfile?.email },
                });
            }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box style={{ textAlign: "center" }}>
                        <Typography variant="h5">
                            {strings.Profile.DEVICE_VERIFICATION}
                        </Typography>
                    </Box>
                    <Box style={{ paddingTop: 10, paddingBottom: 10, lineHeight: 1 }}>
                        <Typography variant="button">
                            {strings.format(
                                strings.Profile.CONFIRM_DEVICE_VERIFICATION_EMAIL,
                                model?.email?.value || "",
                            ).toString()}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        required
                        maxLength={4}
                        variant="outlined"
                        value={model.otpCode?.value}
                        label={strings.Profile.OTP_CODE}
                        errorMessage={model.otpCode?.error}
                        placeholder={strings.Profile.ENTER_OTP_CODE}
                        onChangeValue={(value: any) => onValueChange(value, "otpCode")}
                    />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="space-between">
                    <Box width="49%">
                        <Button fullWidth color="secondary" onClick={handleGetOTP}>
                            {strings.Profile.SEND_TO}
                        </Button>
                    </Box>
                    <Box width="49%">
                        <Button fullWidth color="success" onClick={handleChangeEmail}>
                            {strings.Profile.COMPLETE}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Modal>
    );

    return (
        <Box key={`ce`} id={idHref} p={2}>
            <Typography variant="h5">{strings.Profile.UPDATE_EMAIL}</Typography>
            <Grid container spacing={3} p={2}>
                <Grid item xs={12}>
                    <FormField
                        required
                        maxLength={100}
                        value={model.newEmail?.value}
                        label={strings.Profile.NEW_EMAIL}
                        errorMessage={model.newEmail?.error}
                        placeholder={strings.Profile.ENTER_NEW_EMAIL}
                        onChangeValue={(value: any) => onValueChange(value, "newEmail")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box style={{ float: "right" }}>
                        {!Helpers.isNullOrEmpty(model.newEmail?.value) &&
                            <Button
                                sx={{ mr: 1 }}
                                color="secondary"
                                onClick={() => {
                                    setModel({ email: { value: userProfile?.email } });
                                }}>
                                {strings.Common.CANCEL}
                            </Button>
                        }
                        <Button
                            onClick={handleGetOTP}
                            disabled={Helpers.isNullOrEmpty(model.newEmail?.value)}
                        >
                            {strings.Profile.GET_OTP_CODE}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            {renderModalOTP()}
        </Box>
    );
};

export default ChangeEmail;
